import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faStopwatch, faClipboardList, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { faEye } from '@fortawesome/free-regular-svg-icons';

/**
 * DriverRunBox — one bordered box per driver showing their ordered drops.
 *
 * Drag-and-drop within the box reorders drops (updates drop_number).
 * Dragging a row out of this box onto another DriverRunBox reassigns the route
 * to that driver and recalculates both drivers' drop_numbers.
 *
 * The box also acts as a drop target: a sidebar driver dragged onto it
 * assigns that driver to the route being hovered.
 */
const DriverRunBox = ({
    driverName,
    routes,            // already sorted by drop_number ascending
    theme,
    isLocked,
    showCostings,
    completedRouteUuids,
    onViewCompletion,
    deliveryAlerts,
    showJobComments,
    showJobCostings,
    showStartTime,
    showDemurrage,
    showDeliveryDetails,
    showAddFromDropdown,
    toggleConfirmed,
    contractRoute,
    editSubcontract,
    canEdit,
    onPodOff,
    onRemoveDriver,    // (route_uuid) => void  — removes driver from a route
    deleteRoute,
    removeVehicle,
    vehicleServiceList,
    vehicleOverdueList,
    vorList,
    onReorder,         // (updatedRoutes: [{route_uuid, drop_number}]) => void
    onReassign,        // (route_uuid, newDriverData) => void  — move route to another driver
}) => {
    const vans = ['FE24_AYJ', 'FE24_AYK'];
    const dragItem = useRef(null);      // index being dragged
    const dragOverItem = useRef(null);  // index being dragged over
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const formatTimeToHHMM = (t) => {
        if (!t) return null;
        return t.length <= 5 ? t : t.substring(0, 5);
    };

    // ── Internal reorder drag handlers ────────────────────────────────────────

    const handleDragStart = (e, index, route) => {
        dragItem.current = index;
        // Encode both the reorder payload and the full route so external
        // DriverRunBox components can read who this row belongs to.
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('itemType', 'allocated_route');
        e.dataTransfer.setData('itemData', JSON.stringify({
            route_uuid: route.route_uuid,
            fromDriver: driverName,
            fromDriverId: route.driver_id,
            route,
        }));
    };

    const handleDragEnter = (index) => {
        dragOverItem.current = index;
        setDragOverIndex(index);
    };

    const handleDragEnd = () => {
        dragItem.current = null;
        dragOverItem.current = null;
        setDragOverIndex(null);
    };

    const handleInternalDrop = (e) => {
        e.preventDefault();
        const type = e.dataTransfer.getData('itemType');
        if (type !== 'allocated_route') return; // let RouteContainer handle sidebar drops

        const from = dragItem.current;
        const to = dragOverItem.current;

        if (from === null || to === null || from === to) {
            handleDragEnd();
            return;
        }

        const reordered = [...routes];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);

        // Re-assign sequential drop_numbers (1-based)
        const updated = reordered.map((r, i) => ({ route_uuid: r.route_uuid, drop_number: i + 1 }));
        onReorder(updated, reordered);
        handleDragEnd();
    };

    // ── Styles ────────────────────────────────────────────────────────────────

    const boxBorder = theme === 'dark' ? '2px solid rgba(26,221,247,0.6)' : '2px solid rgba(0,100,200,0.5)';
    const boxBg = theme === 'dark' ? '#1a1a2e' : '#eef4ff';
    const headerBg = theme === 'dark' ? 'rgba(26,221,247,0.15)' : 'rgba(0,100,200,0.1)';

    return (
        <div
            style={{
                border: boxBorder,
                borderRadius: 8,
                marginBottom: 14,
                overflow: 'hidden',
                background: boxBg,
            }}
            // Allow drop from another DriverRunBox (row reassignment)
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
                const type = e.dataTransfer.getData('itemType');
                if (type === 'allocated_route') {
                    try {
                        const data = JSON.parse(e.dataTransfer.getData('itemData'));
                        if (data.fromDriver !== driverName) {
                            // Move to this driver's box — append as last drop
                            onReassign(data.route, driverName);
                        }
                    } catch (_) {}
                }
            }}
        >
            {/* Driver header */}
            <div style={{
                background: headerBg,
                padding: '8px 14px',
                fontWeight: 700,
                fontSize: 15,
                borderBottom: boxBorder,
                color: theme === 'dark' ? 'rgba(26,221,247,0.9)' : 'rgba(0,80,180,0.9)',
            }}>
                {driverName} — {routes.length} drop{routes.length !== 1 ? 's' : ''}
            </div>

            {/* Route rows */}
            <table className="route-table" style={{ width: '100%' }}>
                <thead>
                    <tr>
                        <th style={{ width: 32, background: 'rgba(0,0,0,0.2)' }}>#</th>
                        <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.2)' }}>
                            <FontAwesomeIcon style={{ width: 20, color: 'rgba(7,192,1,0.95)' }} icon={faCheck} />
                        </th>
                        <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.2)' }}>
                            <FontAwesomeIcon style={{ width: 20, color: 'rgba(255,0,0,1)' }} icon={faStopwatch} />
                        </th>
                        <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.2)' }}>
                            <FontAwesomeIcon style={{ width: 20, color: 'rgba(26,157,215,0.95)' }} icon={faClipboardList} />
                        </th>
                        <th style={{ background: 'rgba(0,0,0,0.2)' }}>Customer</th>
                        <th style={{ background: 'rgba(0,0,0,0.2)' }}>Route</th>
                        <th style={{ background: 'rgba(0,0,0,0.2)' }}>Job Comments</th>
                        <th style={{ background: 'rgba(0,0,0,0.2)' }}>Vehicle</th>
                        <th style={{ background: 'rgba(0,0,0,0.2)' }}>Trailer</th>
                        {showCostings && <th style={{ background: 'rgba(0,0,0,0.2)' }}>Start</th>}
                        {showCostings && <th style={{ background: 'rgba(0,0,0,0.2)' }}>Consignment</th>}
                        {showCostings && <th style={{ background: 'rgba(0,0,0,0.2)' }}>Costings</th>}
                    </tr>
                </thead>
                <tbody onDrop={handleInternalDrop} onDragOver={(e) => e.preventDefault()}>
                    {routes.map((route, index) => {
                        const {
                            route_uuid, driver_id, truck_id, truck_reg,
                            trailer_id, trailer_reg, external_trailer,
                            job_comments, route: routeDetails, route_ref,
                            completed, confirmed, is_subbed, demurrage, demurrage_cost,
                            costings, start_time, drop_number, customer_company,
                        } = route;

                        const isCompleted = !!completedRouteUuids?.[route_uuid];
                        const rowBg = isCompleted
                            ? index % 2 === 0 ? 'rgba(26,157,215,0.35)' : 'rgba(56,189,248,0.28)'
                            : is_subbed
                                ? 'rgba(252,186,63,0.8)'
                                : confirmed
                                    ? index % 2 === 0 ? 'rgba(26,221,247,0.8)' : 'rgba(26,221,247,0.6)'
                                    : dragOverIndex === index
                                        ? theme === 'dark' ? 'rgba(26,221,247,0.2)' : 'rgba(0,100,200,0.12)'
                                        : 'transparent';

                        const rowColor = isCompleted
                            ? theme === 'dark' ? '#f8f9fa' : '#333'
                            : (confirmed || is_subbed)
                                ? '#333'
                                : theme === 'light' ? '#333' : '#ddd';

                        return (
                            <tr
                                key={route_uuid}
                                draggable={!isLocked}
                                onDragStart={(e) => handleDragStart(e, index, route)}
                                onDragEnter={() => handleDragEnter(index)}
                                onDragEnd={handleDragEnd}
                                style={{ background: rowBg, color: rowColor, cursor: isLocked ? 'default' : 'grab' }}
                            >
                                {/* Drag handle / drop number */}
                                <td style={{ textAlign: 'center', width: 32, userSelect: 'none' }}>
                                    <span title="Drag to reorder">
                                        <FontAwesomeIcon icon={faGripVertical} style={{ width: 10, opacity: 0.4, marginRight: 4 }} />
                                        {drop_number ?? index + 1}
                                    </span>
                                </td>

                                {/* Status */}
                                <td className="fixed-th" style={{ width: 40, cursor: isLocked ? 'not-allowed' : 'crosshair' }}
                                    onClick={() => {
                                        if (isCompleted) {
                                            onViewCompletion(route_uuid, { driver: driverName, truck_reg, trailer_reg, job_comments, route_ref });
                                        } else if (!isLocked) {
                                            toggleConfirmed(route_uuid, confirmed, is_subbed);
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon style={{ width: 20, color: isCompleted ? (theme === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)') : confirmed || is_subbed ? 'rgba(0,0,0,0.9)' : theme === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }} icon={is_subbed ? undefined : faEye} />
                                </td>

                                {/* Demurrage */}
                                <td className="fixed-th" style={{ width: 40, cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                    title={demurrage ? `${demurrage}: £${demurrage_cost}` : 'Add Demurrage'}
                                    onClick={() => { if (!isLocked) showDemurrage(route_uuid); }}
                                >
                                    <FontAwesomeIcon style={{ width: 20, color: demurrage ? '#ff0000' : theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }} icon={faStopwatch} />
                                </td>

                                {/* Delivery details */}
                                <td className="fixed-th" style={{ width: 40, cursor: 'pointer' }}
                                    title="View / Edit Delivery Details"
                                    onClick={() => showDeliveryDetails(route_uuid)}
                                >
                                    <FontAwesomeIcon icon={faClipboardList} style={{ width: 20, color: deliveryAlerts?.has(route_uuid) ? 'rgba(220,53,69,0.95)' : confirmed || is_subbed ? 'rgba(0,0,0,0.9)' : theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }} />
                                </td>

                                {/* Customer — route actions */}
                                <td className={!isLocked ? 'cell-hoverable' : ''}>
                                    <span style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                        onClick={() => { if (!isLocked) deleteRoute(route_uuid); }}
                                    >{customer_company || '—'}</span>
                                </td>

                                {/* Route */}
                                <td>{routeDetails || 'No route details'}</td>

                                {/* Job Comments */}
                                <td className={!isLocked ? 'cell-hoverable' : ''} style={{ maxWidth: 500, whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                    <span style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                        onClick={() => { if (!isLocked) showJobComments(route_uuid, job_comments); }}
                                    >{job_comments || <span style={{ opacity: 0.35 }}>----</span>}</span>
                                </td>

                                {/* Vehicle */}
                                {truck_reg ? (
                                    <td className={!isLocked ? 'cell-hoverable' : ''}
                                        style={{ backgroundColor: !vans.includes(truck_reg) && vehicleServiceList.includes(truck_reg) ? 'rgba(253,191,58,0.6)' : vehicleOverdueList.includes(truck_reg) ? 'rgba(253,69,58,0.6)' : 'none' }}
                                    >
                                        <span style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                            onClick={() => { if (!isLocked) removeVehicle(route_uuid, 'truck'); }}
                                        >
                                            {truck_reg}{vorList.includes(truck_reg) && <strong> (VOR)</strong>}
                                        </span>
                                    </td>
                                ) : (
                                    <td className="cell-hoverable">
                                        <span style={{ cursor: 'pointer' }}
                                            onClick={() => { if (!isLocked) showAddFromDropdown(route_uuid, 'truck'); }}
                                        >----</span>
                                    </td>
                                )}

                                {/* Trailer */}
                                {trailer_reg ? (
                                    <td className={!isLocked ? 'cell-hoverable' : ''}
                                        style={{ backgroundColor: vehicleServiceList.includes(trailer_reg) ? 'rgba(253,191,58,0.6)' : vehicleOverdueList.includes(trailer_reg) ? 'rgba(253,69,58,0.6)' : 'none' }}
                                    >
                                        <span style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                            onClick={() => { if (!isLocked) removeVehicle(route_uuid, 'trailer'); }}
                                        >{trailer_id === 1 ? external_trailer : trailer_reg}</span>
                                    </td>
                                ) : (
                                    <td className="cell-hoverable">
                                        <span style={{ cursor: 'pointer' }}
                                            onClick={() => { if (!isLocked) showAddFromDropdown(route_uuid, 'trailer'); }}
                                        >----</span>
                                    </td>
                                )}

                                {showCostings && (
                                    <td className="cell-hoverable">
                                        <span style={{ cursor: 'pointer' }}
                                            onClick={() => showStartTime(route_uuid, start_time)}
                                        >{formatTimeToHHMM(start_time) || '----'}</span>
                                    </td>
                                )}
                                {showCostings && <td>{route_ref || '----'}</td>}
                                {showCostings && (
                                    <td className="cell-hoverable">
                                        <span style={{ cursor: 'pointer' }}
                                            onClick={() => showJobCostings(route_uuid, costings)}
                                        >{costings ? '£' + costings : '----'}</span>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default DriverRunBox;
